import React, { FC, useEffect, useRef, useState } from 'react';

import { useMobileControls } from '../engine/useMobileControls';

interface MobileControlsProps {
  onControlsChange: (controls: any) => void;
}

export const MobileControls: FC<MobileControlsProps> = ({
  onControlsChange,
}) => {
  const { controls, isMobile, handleButtonPress, handleButtonRelease } =
    useMobileControls();
  const [activeButtons, setActiveButtons] = useState<{
    jump: boolean;
    rasengan: boolean;
    shuriken: boolean;
  }>({
    jump: false,
    rasengan: false,
    shuriken: false,
  });

  // Pass controls up to parent component - simplified to avoid circular dependencies
  useEffect(() => {
    // Always notify parent of controls - the parent component will decide what to do with them
    onControlsChange(controls);
  }, [controls, onControlsChange]);

  if (!isMobile) return null;

  // Base styles for the action buttons
  const buttonStyle: React.CSSProperties = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.3)',
    border: '2px solid white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
    fontSize: '18px',
    fontWeight: 'bold',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'none',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.1s, box-shadow 0.1s',
  };

  // Active state styles (when button is pressed)
  const activeButtonStyle: React.CSSProperties = {
    transform: 'scale(0.95) translateY(2px)',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  };

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    right: '30px',
    bottom: '50px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    zIndex: 1000,
  };

  const actionButtonsContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '20px',
  };

  // Event handlers with touch events and active state tracking
  const handleTouchStart =
    (action: 'jump' | 'rasengan' | 'shuriken') => (e: React.TouchEvent) => {
      e.preventDefault();
      handleButtonPress(action);
      setActiveButtons((prev) => ({ ...prev, [action]: true }));

      // Add vibration feedback if supported
      if (navigator.vibrate) {
        navigator.vibrate(20);
      }
    };

  const handleTouchEnd =
    (action: 'jump' | 'rasengan' | 'shuriken') => (e: React.TouchEvent) => {
      e.preventDefault();
      handleButtonRelease(action);
      setActiveButtons((prev) => ({ ...prev, [action]: false }));
    };

  return (
    <>
      {/* Action buttons container */}
      <div style={containerStyle}>
        {/* Jump button */}
        <div
          style={{
            ...buttonStyle,
            background: 'rgba(255, 255, 255, 0.1)',
            ...(activeButtons.jump ? activeButtonStyle : {}),
          }}
          onTouchStart={handleTouchStart('jump')}
          onTouchEnd={handleTouchEnd('jump')}
        >
          JUMP
        </div>

        <div style={actionButtonsContainerStyle}>
          {/* Rasengan button */}
          <div
            style={{
              ...buttonStyle,
              background: 'rgba(255, 255, 255, 0.1)',
              ...(activeButtons.rasengan ? activeButtonStyle : {}),
            }}
            onTouchStart={handleTouchStart('rasengan')}
            onTouchEnd={handleTouchEnd('rasengan')}
          >
            R
          </div>

          {/* Shuriken button */}
          <div
            style={{
              ...buttonStyle,
              background: 'rgba(255, 255, 255, 0.1)',
              ...(activeButtons.shuriken ? activeButtonStyle : {}),
            }}
            onTouchStart={handleTouchStart('shuriken')}
            onTouchEnd={handleTouchEnd('shuriken')}
          >
            S
          </div>
        </div>
      </div>
    </>
  );
};
